import { useState } from 'react';
import { IonButton, IonIcon, IonInput, IonItem, IonList, IonText } from '@ionic/react';
import { addCircleOutline, alertCircleOutline, closeOutline, linkOutline } from 'ionicons/icons';
import { useAppConfig } from '../../../../hooks/useAppConfig';

interface PostLinkFieldsProps {
  url: string;
  title: string;
  onUrlChange: (value: string) => void;
  onTitleChange: (value: string) => void;
  // Spanish validation message from getPostLinkError, shown below the inputs.
  error?: string;
  disabled?: boolean;
}

// Pure form piece for a post's optional "Enlace relacionado": the URL plus an
// optional title shown instead of the bare URL. Collapsed behind an "Añadir
// enlace relacionado" action so it doesn't compete with the editor; it is shown
// expanded whenever the post already has a link (e.g. when editing).
export const PostLinkFields = ({
  url,
  title,
  onUrlChange,
  onTitleChange,
  error,
  disabled = false,
}: PostLinkFieldsProps) => {
  const { postMaxLinkTitleLength } = useAppConfig();
  // Starts open when the post already has a link, so clearing the fields by hand
  // doesn't collapse them mid-edit; only "Quitar" collapses.
  const [opened, setOpened] = useState(url !== '' || title !== '');
  const expanded = opened || url !== '' || title !== '';

  // Clearing both fields removes the link (it is sent as null on save).
  const removeLink = () => {
    onUrlChange('');
    onTitleChange('');
    setOpened(false);
  };

  // A clear IonButton: reads as a button with the same pressed/disabled states
  // as the composer's other actions, but lighter so it doesn't compete with them.
  if (!expanded) {
    return (
      <IonButton
        fill="clear"
        size="small"
        className="self-start"
        // No start padding so the icon lines up with the editor's left edge.
        style={{ '--padding-start': '0px' }}
        onClick={() => setOpened(true)}
        disabled={disabled}
      >
        {/* Inline margin: Tailwind's base reset zeroes Ionic's slotted-icon gap. */}
        <IonIcon
          icon={addCircleOutline}
          slot="start"
          aria-hidden="true"
          style={{ marginInlineEnd: '6px' }}
        />
        Añadir enlace relacionado
      </IonButton>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <p className="text-[13px] font-semibold text-[#8a93a3]">Enlace relacionado</p>
        <IonButton
          fill="clear"
          size="small"
          color="medium"
          // No end padding so the icon lines up with the box's right edge.
          style={{ '--padding-end': '0px' }}
          onClick={removeLink}
          disabled={disabled}
        >
          Quitar
          {/* Inline margin: Tailwind's base reset zeroes Ionic's slotted-icon gap. */}
          <IonIcon
            icon={closeOutline}
            slot="end"
            aria-hidden="true"
            style={{ marginInlineStart: '4px' }}
          />
        </IonButton>
      </div>
      <div className="overflow-hidden border border-[#e2e7ef]" style={{ borderRadius: '12px' }}>
        <IonList lines="full" className="py-0">
          <IonItem>
            <IonIcon icon={linkOutline} slot="start" aria-hidden="true" className="text-[#8a93a3]" />
            <IonInput
              type="url"
              inputmode="url"
              aria-label="Enlace relacionado"
              placeholder="https://…"
              value={url}
              clearInput
              disabled={disabled}
              onIonInput={(e) => onUrlChange(e.detail.value ?? '')}
            />
          </IonItem>
          <IonItem lines="none">
            <IonInput
              aria-label="Título del enlace (opcional)"
              placeholder="Título (opcional)"
              value={title}
              maxlength={postMaxLinkTitleLength}
              counter
              disabled={disabled}
              onIonInput={(e) => onTitleChange(e.detail.value ?? '')}
            />
          </IonItem>
        </IonList>
      </div>
      {error && (
        <IonText color="danger" className="block">
          <p className="flex items-center gap-1.5 text-sm">
            <IonIcon icon={alertCircleOutline} aria-hidden="true" />
            {error}
          </p>
        </IonText>
      )}
    </div>
  );
};
